import { Component, effect, inject, signal } from '@angular/core';
import { DialogActionsComponent, DialogContentBase, DialogRef } from '@progress/kendo-angular-dialog';
import { NotificationService } from '@progress/kendo-angular-notification';
import { TranslateModule, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DropDownFilterSettings } from '@progress/kendo-angular-dropdowns';
import { distinctUntilChanged } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { ButtonComponent } from '@progress/kendo-angular-buttons';
import {
  CapitalizePipe,
  ClientOrder,
  ClientOrderService,
  errorToast,
  FormErrorService,
  successSaveToast,
  SupplierInvoice,
  SupplierInvoiceSection,
  SupplierInvoiceSectionService,
} from 'shared';
import {
  ClientOrderTableComponent
} from '@features/purchase/supplier-invoices/supplier-invoice-order-link-popup/client-order-table/client-order-table.component';
import { UpperCasePipe } from '@angular/common';

@Component({
  selector: 'rc-supplier-invoice-order-link-popup',
  imports: [
    ButtonComponent,
    CapitalizePipe,
    DialogActionsComponent,
    ReactiveFormsModule,
    TranslateModule,
    CapitalizePipe,
    CapitalizePipe,
    ClientOrderTableComponent,
    UpperCasePipe,
  ],
  providers: [FormErrorService, TranslateService, TranslatePipe, CapitalizePipe],
  templateUrl: './supplier-invoice-order-link-popup.html',
  styleUrl: './supplier-invoice-order-link-popup.scss',
})
export class SupplierInvoiceOrderLinkPopupComponent extends DialogContentBase {
  #supplierInvoiceSectionService = inject(SupplierInvoiceSectionService);
  #notificationService = inject(NotificationService);
  #clientOrderService = inject(ClientOrderService);
  #formErrorService = inject(FormErrorService);
  #capitalizePipe = inject(CapitalizePipe);
  #translateService = inject(TranslateService);

  selectedClientOrder = signal<ClientOrder | undefined>(undefined);
  supplierInvoiceSection = signal<SupplierInvoiceSection | undefined>(undefined);
  supplierInvoice = signal<SupplierInvoice | undefined>(undefined);
  supplierInvoiceOrderLinkForm: FormGroup<SupplierInvoiceOrderLinkForm> = this.#initForm();

  filterSettings: DropDownFilterSettings = {
    caseSensitive: false,
    operator: 'contains',
  };

  constructor() {
    const dialog = inject(DialogRef);

    super(dialog);

    effect(() => {
      const si = this.supplierInvoiceSection();
      if (si) {
        if (si.clientOrder) {
          this.#clientOrderService.getClientOrderByIri(si.clientOrder).subscribe((clientOrder) => {
            this.selectedClientOrder.set(clientOrder);
          });
        }
        this.supplierInvoiceOrderLinkForm.patchValue({
          clientOrder: si.clientOrder,
        });
      }
    });
  }

  #initForm() {
    const form = new FormGroup<SupplierInvoiceOrderLinkForm>({
      clientOrder: new FormControl(null, {
        nonNullable: false,
        validators: [],
      }),
    });
    form.updateValueAndValidity();
    return form;
  }

  onSubmit() {
    this.#supplierInvoiceSectionService
      .patch(this.supplierInvoiceSection()!['@id']!, this.supplierInvoiceOrderLinkForm.value as SupplierInvoiceSection)
      .pipe(take(1), distinctUntilChanged())
      .subscribe({
        next: (response) => {
          const notification = this.#notificationService.show(successSaveToast);
          if (notification.content) {
            notification.content.instance.value = this.#capitalizePipe.transform(
              this.#translateService.instant('SUPPLIER_INVOICE') +
                ': ' +
                this.supplierInvoiceSection()!.supplierInvoiceDocumentNumber,
            );
          }
          this.supplierInvoiceSection.set(response);
          this.dialog.close({ isSuccessFull: true, result: response });
        },
        error: (err: HttpErrorResponse) => {
          const notification = this.#notificationService.show(errorToast);
          if (notification.content) {
            notification.content.instance.setCurrentValue(err);
          }
          this.#formErrorService.setErrorViolations(err);
        },
      });
  }

  patchClientOrder(event?: ClientOrder) {
    this.selectedClientOrder.set(event);
    this.supplierInvoiceOrderLinkForm.patchValue({
      clientOrder: event ? event['@id'] : null,
    });
  }

  close() {
    this.supplierInvoiceOrderLinkForm.reset();
    this.dialog.close();
  }
}

interface SupplierInvoiceOrderLinkForm {
  clientOrder: FormControl<string | null>;
}
